import {FormEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {useEscopo} from '../ServiceLevelContext'
import {hasServiceLevelPermission} from '../permissions'
import {Grid3x3, X} from 'lucide-react'
import {getSlaMatrix, salvarSla, SlaMatrix} from '../api'
import {Seller, Regiao} from '../types'
import useDialog from '../components/useDialog'

interface Edicao {
  seller: Seller
  regiao: Regiao
  prazo: number | string
  tipo: 'CORRIDAS' | 'UTEIS'
  novo: boolean
}

export default function AdminRegioesSlas() {
  const podeOutraTela = hasServiceLevelPermission(useEscopo(), 'sla_sellers')
  const [data, setData] = useState<SlaMatrix | null>(null)
  const [editando, setEditando] = useState<Edicao | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [busca, setBusca] = useState('')
  const [regiaoId, setRegiaoId] = useState('')
  const [erro, setErro] = useState('')
  const [erroEdicao, setErroEdicao] = useState('')
  const [sucesso, setSucesso] = useState('')
  const panel = useRef<HTMLDivElement>(null)
  const fechar = () => {
    if (!salvando) setEditando(null)
  }
  useDialog(!!editando, panel, fechar, !salvando)
  const load = useCallback(async () => {
    const next = await getSlaMatrix()
    setData(next)
    setErro('')
  }, [])
  useEffect(() => {
    load().catch((error) =>
      setErro(
        error instanceof Error ? error.message : 'Não foi possível carregar a matriz de SLAs.'
      )
    )
  }, [load])
  const slaMap = useMemo(
    () => new Map(data?.slas.map((sla) => [`${sla.seller_id}-${sla.regiao_id}`, sla]) ?? []),
    [data]
  )
  const abrirEdicao = (seller: Seller, regiao: Regiao) => {
    if (!data) return
    const atual = slaMap.get(`${seller.seller_id}-${regiao.id}`)
    setErroEdicao('')
    setSucesso('')
    setEditando({
      seller,
      regiao,
      prazo: (atual?.prazo_horas ?? data.default_horas) / 24,
      tipo: atual?.tipo_contagem ?? 'CORRIDAS',
      novo: !atual,
    })
  }
  const prazoValido =
    editando && Number.isInteger(Number(editando.prazo)) && Number(editando.prazo) >= 1
  const salvar = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editando || !prazoValido || salvando) return
    setSalvando(true)
    setErroEdicao('')
    try {
      await salvarSla(
        editando.seller.seller_id,
        editando.regiao.id,
        Number(editando.prazo) * 24,
        editando.tipo
      )
      setSucesso(`SLA de ${editando.seller.nome_fantasia} para ${editando.regiao.nome} atualizado.`)
      try {
        await load()
      } catch {
        setErro('O SLA foi salvo. Não foi possível atualizar a matriz; tente carregar novamente.')
      }
      setEditando(null)
    } catch (error) {
      setErroEdicao(
        error instanceof Error ? error.message : 'Não foi possível salvar o SLA. Tente novamente.'
      )
    } finally {
      setSalvando(false)
    }
  }
  if (!data)
    return (
      <div className='card'>
        {erro ? (
          <div className='feedback error' role='alert'>
            {erro}
            <button
              type='button'
              className='btn secondary'
              onClick={() =>
                load().catch((error) =>
                  setErro(error instanceof Error ? error.message : 'Falha ao carregar.')
                )
              }
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <p className='empty' role='status'>
            Carregando matriz de SLAs…
          </p>
        )}
      </div>
    )
  const faltantes = Math.max(0, data.sellers.length * data.regioes.length - slaMap.size)
  const query = busca.trim().toLocaleLowerCase('pt-BR')
  const sellers = data.sellers.filter(
    (seller) =>
      !query ||
      `${seller.nome_fantasia} ${seller.razao_social} ${seller.uf_base}`
        .toLocaleLowerCase('pt-BR')
        .includes(query)
  )
  const regioes = data.regioes.filter((regiao) => !regiaoId || String(regiao.id) === regiaoId)
  const prazoLabel = (seller: Seller, regiao: Regiao) => {
    const sla = slaMap.get(`${seller.seller_id}-${regiao.id}`)
    return sla
      ? `${sla.prazo_horas / 24} dias ${sla.tipo_contagem === 'UTEIS' ? 'úteis' : 'corridos'}`
      : `${data.default_horas / 24} dias (padrão)`
  }

  return (
    <>
      <div className='page-header'>
        <div>
          <h1 className='flex items-center gap-2'>
            <Grid3x3 size={24} /> Regiões e SLAs
          </h1>
          <div className='sub'>
            Personalize o prazo de envio de cada seller por região.{' '}
            {podeOutraTela && (
              <Link to='/service-level/admin/sla-sellers'>Aplicar a todas as regiões →</Link>
            )}
          </div>
        </div>
      </div>
      <div className='admin-summary'>
        <div>
          <strong>{data.regioes.length}</strong>
          <span>Regiões atendidas</span>
        </div>
        <div>
          <strong>{slaMap.size}</strong>
          <span>SLAs configurados</span>
        </div>
        <div>
          <strong>{faltantes}</strong>
          <span>Combinações com prazo padrão</span>
        </div>
      </div>
      {sucesso && (
        <p className='feedback success' role='status'>
          {sucesso}
        </p>
      )}
      {erro && (
        <div className='feedback error' role='alert'>
          {erro}
          <button
            type='button'
            className='btn secondary'
            onClick={() => load().catch(() => setErro('Não foi possível atualizar a matriz.'))}
          >
            Carregar novamente
          </button>
        </div>
      )}
      <section className='card'>
        <h2>Regiões atendidas</h2>
        <div className='admin-region-list'>
          {data.regioes.map((regiao) => (
            <div key={regiao.id} className='admin-region-card'>
              <strong>{regiao.nome}</strong>
              <div>
                {regiao.ufs.map((uf) => (
                  <span key={uf} className='admin-region-chip'>
                    {uf}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        {data.regioes.length === 0 && <p className='empty'>Nenhuma região cadastrada.</p>}
      </section>
      <section className='card'>
        <div className='page-header'>
          <div>
            <h2>Matriz de prazos</h2>
            <p className='chart-note'>
              Selecione um prazo para editar. Combinações sem configuração usam{' '}
              {data.default_horas / 24} dias corridos.
            </p>
          </div>
          <div className='filters'>
            <label className='filter-field' htmlFor='matrix-seller-search'>
              <span>Buscar seller</span>
              <input
                id='matrix-seller-search'
                type='search'
                placeholder='Nome ou UF…'
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
              />
            </label>
            <label className='filter-field' htmlFor='matrix-region-filter'>
              <span>Região</span>
              <select
                id='matrix-region-filter'
                value={regiaoId}
                onChange={(event) => setRegiaoId(event.target.value)}
              >
                <option value=''>Todas as regiões</option>
                {data.regioes.map((regiao) => (
                  <option key={regiao.id} value={regiao.id}>
                    {regiao.nome}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div
          className='table-wrap desktop-only'
          tabIndex={0}
          role='region'
          aria-label='Matriz de SLA por seller e região; role horizontalmente para ver todas as regiões'
        >
          <table>
            <thead>
              <tr>
                <th scope='col'>Seller</th>
                {regioes.map((regiao) => (
                  <th key={regiao.id} scope='col'>
                    {regiao.nome}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sellers.map((seller) => (
                <tr key={seller.seller_id}>
                  <th scope='row'>
                    <strong>{seller.nome_fantasia}</strong>
                    <div className='muted text-xs'>{seller.uf_base}</div>
                  </th>
                  {regioes.map((regiao) => {
                    const sla = slaMap.get(`${seller.seller_id}-${regiao.id}`)
                    return (
                      <td key={regiao.id}>
                        <button
                          type='button'
                          className={`sla-cell ${sla ? '' : 'missing'}`}
                          data-analytics-feature='Nível de Serviço · Editar SLA por região'
                          onClick={() => abrirEdicao(seller, regiao)}
                          aria-label={`Editar SLA de ${seller.nome_fantasia} em ${
                            regiao.nome
                          }: ${prazoLabel(seller, regiao)}`}
                          title={
                            sla ? `Vigente desde ${sla.vigencia_inicio}` : 'Prazo padrão global'
                          }
                        >
                          {prazoLabel(seller, regiao)}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='mobile-only admin-matrix-mobile'>
          {sellers.map((seller) => (
            <article
              key={seller.seller_id}
              className='admin-seller-card'
              aria-label={`SLAs de ${seller.nome_fantasia}`}
            >
              <div className='admin-seller-head'>
                <strong>{seller.nome_fantasia}</strong>
                <span className='badge CAPTADO'>{seller.uf_base}</span>
              </div>
              {regioes.map((regiao) => {
                const sla = slaMap.get(`${seller.seller_id}-${regiao.id}`)
                return (
                  <button
                    key={regiao.id}
                    type='button'
                    className='admin-region-sla'
                    data-analytics-feature='Nível de Serviço · Editar SLA por região'
                    onClick={() => abrirEdicao(seller, regiao)}
                    aria-label={`Editar SLA de ${seller.nome_fantasia} em ${
                      regiao.nome
                    }: ${prazoLabel(seller, regiao)}`}
                  >
                    <span>{regiao.nome}</span>
                    <span className={`badge ${sla ? 'FINALIZADO' : 'default-sla'}`}>
                      {prazoLabel(seller, regiao)}
                    </span>
                    <span className='admin-edit-label'>Editar</span>
                  </button>
                )
              })}
            </article>
          ))}
        </div>
        {sellers.length === 0 && (
          <p className='empty'>Nenhum seller encontrado. Tente outro nome ou UF.</p>
        )}
        <p className='chart-note'>
          Cada alteração cria uma nova vigência. Os pedidos passados mantêm o SLA válido na época.
        </p>
      </section>
      {editando && (
        <div
          className='modal-overlay'
          onClick={(event) => {
            if (event.target === event.currentTarget) fechar()
          }}
        >
          <div
            className='modal'
            ref={panel}
            role='dialog'
            aria-modal='true'
            aria-labelledby='edit-sla-title'
            aria-describedby='edit-sla-description'
            tabIndex={-1}
          >
            <div className='modal-heading'>
              <h2 id='edit-sla-title'>{editando.novo ? 'Configurar' : 'Editar'} SLA</h2>
              <button
                type='button'
                className='icon-button'
                aria-label='Fechar edição de SLA'
                onClick={fechar}
                disabled={salvando}
              >
                <X size={20} />
              </button>
            </div>
            <p id='edit-sla-description' className='sub mb-4'>
              <strong>{editando.seller.nome_fantasia}</strong> · {editando.regiao.nome}
            </p>
            <form onSubmit={salvar}>
              <div className='form-group'>
                <label htmlFor='edit-sla-days'>Prazo de envio (dias)</label>
                <input
                  id='edit-sla-days'
                  data-autofocus
                  type='number'
                  inputMode='numeric'
                  min='1'
                  step='1'
                  required
                  value={editando.prazo}
                  disabled={salvando}
                  aria-invalid={editando.prazo !== '' && !prazoValido}
                  aria-describedby={!prazoValido ? 'edit-sla-days-error' : undefined}
                  onChange={(event) => {
                    setEditando((previous) =>
                      previous ? {...previous, prazo: event.target.value} : previous
                    )
                    setErroEdicao('')
                  }}
                />
                {!prazoValido && (
                  <p id='edit-sla-days-error' className='field-error'>
                    Informe um número inteiro de dias maior que zero.
                  </p>
                )}
              </div>
              <div className='form-group'>
                <label htmlFor='edit-sla-type'>Tipo de contagem</label>
                <select
                  id='edit-sla-type'
                  value={editando.tipo}
                  disabled={salvando}
                  onChange={(event) =>
                    setEditando((previous) =>
                      previous
                        ? {...previous, tipo: event.target.value as 'CORRIDAS' | 'UTEIS'}
                        : previous
                    )
                  }
                >
                  <option value='CORRIDAS'>Dias corridos</option>
                  <option value='UTEIS'>Dias úteis</option>
                </select>
              </div>
              <p className='chart-note'>
                A alteração será aplicada aos novos pedidos desta combinação de seller e região.
              </p>
              {erroEdicao && (
                <p className='feedback error' role='alert'>
                  {erroEdicao}
                </p>
              )}
              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn secondary'
                  onClick={fechar}
                  disabled={salvando}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='btn'
                  data-analytics-feature='Nível de Serviço · Salvar SLA por região'
                  disabled={salvando || !prazoValido}
                >
                  {salvando ? 'Salvando…' : 'Salvar prazo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
